import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { TextField, Grid, Box, Autocomplete } from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useLocation } from "react-router-dom";
import useSales from "hooks/useSales";
import useDealer from "hooks/useDealer";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const initialState = {
  name: "",
  countryCode: "+91",
  mobile: "",
  email: "",
  role: "SALES_REP",
  dealer: [],
  target: [],
};

const SalesTeamForm = () => {
  const { loading, getSalesById, updateSales, createSales } = useSales();
  const { getdealerOptionList } = useDealer();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const salesId = queryParams.get("id");
  const [formData, setFormData] = useState(initialState);
  const [dealerOptions, setDealerOptions] = useState([]);
  const [errors, setErrors] = useState({});

  const handleSalesChange = (event, value) => {
    setFormData((prevState) => ({
      ...prevState,
      dealer: value.map((dealer) => dealer._id),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.email) newErrors.email = "Email is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (salesId) {
        const updateResponse = await updateSales(salesId, formData);
        if (updateResponse?.success) {
          setFormData(initialState);
          navigate("/sales-team");
        } else {
          alert(updateResponse?.message);
        }
        return;
      }
      const createResponse = await createSales(formData);
      if (createResponse?.success) {
        setFormData(initialState);
        navigate("/sales-team");
      } else {
        alert(createResponse?.message);
      }
    }
  };

  const fetchSales = async () => {
    const response = await getSalesById(salesId);
    if (response?.success) {
      setFormData({
        name: response.data.userId.name,
        countryCode: response.data.userId.countryCode,
        mobile: response.data.userId.mobile,
        email: response.data.userId.email,
        dealer: response.data.dealer.map((dealer) => dealer._id),
        target: response.data.target || [],
      });
    }
  };

  const fetchDealerList = async () => {
    const response = await getdealerOptionList();
    if (response.success) {
      setDealerOptions(response.data);
    }
  };

  useEffect(() => {
    fetchDealerList();
    if (salesId) {
      fetchSales();
    }
  }, [salesId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: `calc(100vh - 120px)`,
        p: 4,
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <MDTypography variant="h5" component="h1" align="center" gutterBottom>
        Sales Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill out the details below to register/update Sales.
      </MDTypography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Mobile
          </MDTypography>
          <TextField
            fullWidth
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            required
            variant="outlined"
            error={!!errors.mobile}
            helperText={errors.mobile}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Email
          </MDTypography>
          <TextField
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            variant="outlined"
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Dealer
          </MDTypography>
          <Autocomplete
            multiple
            name="dealer"
            value={dealerOptions.filter((dealer) => formData.dealer.includes(dealer._id))}
            onChange={handleSalesChange}
            id="checkboxes-tags-demo"
            options={dealerOptions}
            disableCloseOnSelect
            getOptionLabel={(option) => {
              return (
                (option?.userId?.name || "") +
                " " +
                (option?.bussinessName ? `(${option?.bussinessName})` : "")
              );
            }}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              return (
                <li {...optionProps}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option?.userId?.name} {option?.bussinessName && `(${option?.bussinessName})`}
                </li>
              );
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </Grid>
        <Grid item xs={12}>
          <MDBox>
            <MDButton
              type="submit"
              variant="contained"
              color="dark"
              fullWidth
              sx={{ marginTop: "3rem" }}
              onClick={handleSubmit}
            >
              {loading ? "Submitting..." : salesId ? "Update Sales" : "Submit"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Box>
  );
};

SalesTeamForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default SalesTeamForm;
